import React from "react";
import { styled } from "@mui/material/styles";
import { Box, Typography, CircularProgress } from "@mui/material";
import CollapseCard from "components/Collapse";
import TwoColumnContainer from "components/Container/TwoColumnContainer";
import { formatTimestamp, formatDuration } from "utils/format";

const StyledTypography = styled((props) => (
  <Typography component="div" {...props} />
))(({ theme }) => ({
  fontSize: theme.typography.customFontSize[2],
  color: theme.palette.primary.black,
  fontWeight: theme.typography.customFontWeight[1],
}));

const SurveillancePanel = ({
  infoData,
  networkData,
  timeZone,
  batchLoading,
}) => {
  const isNvr = infoData.deviceType === "nvr";

  const getUptimeValue = () => {
    const status = infoData.linkStatus;
    const uptime = infoData.uptime;
    const lastSeen = infoData.lastOfflineTime;

    if (status === "offline") {
      return lastSeen !== null ? formatTimestamp(lastSeen) : "-";
    } else if (status === "online") {
      return uptime !== null
        ? formatDuration(Math.max(0, Math.floor(Date.now() / 1000) - uptime))
        : "-";
    }
    return "-";
  };

  // Storage Info default Fallback (以符合設計圖)
  const disks = infoData.storageInfo?.disks ?? [
    { name: "DISK 1", status: "Normal" },
    { name: "DISK 2", status: "Normal" },
  ];
  const freeSpace = infoData.storageInfo?.freeSpace ?? "500 GB";

  // Alarm Info default Fallback (以符合設計圖)
  const alarmIn = infoData.alarmIoInfo?.alarmIn ?? Array.from({ length: 8 }, (_, i) => ({
    id: i + 1,
    status: "Norma State N/C",
  }));
  const alarmOut = infoData.alarmIoInfo?.alarmOut ?? [
    { id: 1, validSignal: "Open", mode: "Switch Mode" },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "20px", width: "100%" }}>
      {/* Device Panel */}
      <CollapseCard title="Device">
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <TwoColumnContainer label="Site">
            <StyledTypography>{infoData.siteName ?? "-"}</StyledTypography>
          </TwoColumnContainer>
          <TwoColumnContainer label="Device Type">
            <StyledTypography>{isNvr ? "NVR" : "IPC"}</StyledTypography>
          </TwoColumnContainer>
          {isNvr && (
            <TwoColumnContainer label="Max Channels">
              <StyledTypography>{infoData.maxChannels ?? 8}</StyledTypography>
            </TwoColumnContainer>
          )}
          <TwoColumnContainer label="Model">
            <StyledTypography>{infoData.modelName ?? "-"}</StyledTypography>
          </TwoColumnContainer>
          <TwoColumnContainer label="Firmware Version">
            <StyledTypography>{infoData.firmwareVersion ?? "-"}</StyledTypography>
          </TwoColumnContainer>
          <TwoColumnContainer label="SN">
            <StyledTypography>{infoData.serialNumber ?? "-"}</StyledTypography>
          </TwoColumnContainer>
          <TwoColumnContainer label="IP Address">
            <StyledTypography>{infoData.localIpAddr ?? networkData?.localIp ?? "-"}</StyledTypography>
          </TwoColumnContainer>
          <TwoColumnContainer label="MAC Address">
            <StyledTypography>{infoData.macAddress ?? "-"}</StyledTypography>
          </TwoColumnContainer>
          <TwoColumnContainer label="Uptime">
            <StyledTypography>{getUptimeValue()}</StyledTypography>
          </TwoColumnContainer>
          <TwoColumnContainer label="Time Zone">
            <StyledTypography>
              {batchLoading ? <CircularProgress size={16} /> : (timeZone || infoData.timeZone || "-")}
            </StyledTypography>
          </TwoColumnContainer>
          <TwoColumnContainer label="Joined On">
            <StyledTypography>
              {infoData?.joinedOn ? formatTimestamp(infoData.joinedOn) : "-"}
            </StyledTypography>
          </TwoColumnContainer>
        </Box>
      </CollapseCard>

      {/* Storage Info Panel (NVR Only) */}
      {isNvr && (
        <CollapseCard title="Storage Info">
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <TwoColumnContainer label="Disk Status">
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {disks.map((disk, idx) => (
                  <StyledTypography key={idx}>
                    {disk.name}: {disk.status}
                  </StyledTypography>
                ))}
              </Box>
            </TwoColumnContainer>
            <TwoColumnContainer label="Disk Free Space">
              <StyledTypography>{freeSpace}</StyledTypography>
            </TwoColumnContainer>
          </Box>
        </CollapseCard>
      )}

      {/* Alarm I/O Info Panel (NVR Only) */}
      {isNvr && (
        <CollapseCard title="Alarm I/O Info">
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <TwoColumnContainer label="Alarm in Status">
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {alarmIn.map((alarm) => (
                  <StyledTypography key={alarm.id}>
                    Alarm in {alarm.id}: {alarm.status}
                  </StyledTypography>
                ))}
              </Box>
            </TwoColumnContainer>
            <TwoColumnContainer label="Alarm out Status">
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {alarmOut.map((alarm, idx) => (
                  <Box key={idx} sx={{ display: "flex", flexDirection: "column", gap: 0.2 }}>
                    <StyledTypography>
                      Alarm out {alarm.id}:
                    </StyledTypography>
                    <StyledTypography>
                      Valid Signal: {alarm.validSignal}
                    </StyledTypography>
                    <StyledTypography>
                      Alarm out Mode: {alarm.mode}
                    </StyledTypography>
                  </Box>
                ))}
              </Box>
            </TwoColumnContainer>
          </Box>
        </CollapseCard>
      )}
    </Box>
  );
};

export default SurveillancePanel;
